import { OrderItem } from '@/types/order-list';
import { useMembershipPreviewMode } from '@/components/membership/MembershipContentLock';
import {
  closestCenter,
  DndContext,
  DragEndEvent,
  KeyboardSensor,
  MouseSensor,
  TouchSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import { restrictToHorizontalAxis } from '@dnd-kit/modifiers';
import { arrayMove } from '@dnd-kit/sortable';
import {
  ColumnDef,
  ColumnSizingState,
  getCoreRowModel,
  getSortedRowModel,
  OnChangeFn,
  useReactTable,
} from '@tanstack/react-table';
import {
  CSSProperties,
  ReactNode,
  useCallback,
  useLayoutEffect,
  useRef,
} from 'react';
import { TableBody, TableHeader } from './';
import { TABLE_CHECKBOX_COLUMN_ID, TABLE_SPLIT_HEADER_Z_INDEX } from './tableConstants';
import { useOrderStickyLayout } from '../../_contexts/OrderStickyLayoutContext';

export { TABLE_CHECKBOX_COLUMN_ID };

interface MarketTableProps<TData> {
  orderList: TData[];
  columns: ColumnDef<TData>[];
  columnOrder: string[];
  setColumnOrder: OnChangeFn<string[]>;
  checkedOrders?: OrderItem[];
  bodyFooter?: ReactNode;
  /** 컬럼 너비 조절 활성화 + 너비 상태(영속화). 주문 테이블에서만 사용. */
  enableColumnResize?: boolean;
  columnSizing?: ColumnSizingState;
  onColumnSizingChange?: OnChangeFn<ColumnSizingState>;
  /** 행 클릭 시 그 행의 체크박스를 토글한다. */
  enableRowClickSelect?: boolean;
}

const MarketTable = <TData extends object>({
  orderList,
  columns,
  columnOrder,
  setColumnOrder,
  checkedOrders,
  bodyFooter,
  enableColumnResize = false,
  columnSizing,
  onColumnSizingChange,
  enableRowClickSelect = false,
}: MarketTableProps<TData>) => {
  const isPreviewMode = useMembershipPreviewMode();
  const { tableHeaderTop } = useOrderStickyLayout();
  const bodyScrollRef = useRef<HTMLDivElement | null>(null);
  const splitHeaderContentRef = useRef<HTMLDivElement | null>(null);

  // 각 컬럼의 최소 너비 = 헤더 텍스트(+정렬버튼) 실측값. 그 이하로는 resize 되지 않게 clamp.
  const headerMinSizeRef = useRef<Record<string, number>>({});
  const reportHeaderMinSize = useCallback((id: string, minWidth: number) => {
    headerMinSizeRef.current[id] = minWidth;
  }, []);
  const handleColumnSizingChange = useCallback<OnChangeFn<ColumnSizingState>>(
    (updater) => {
      onColumnSizingChange?.((prev) => {
        const next = typeof updater === 'function' ? updater(prev) : updater;
        const clamped: ColumnSizingState = {};
        for (const id of Object.keys(next)) {
          const min = headerMinSizeRef.current[id];
          clamped[id] = min ? Math.max(next[id], min) : next[id];
        }
        return clamped;
      });
    },
    [onColumnSizingChange],
  );

  const table = useReactTable({
    data: orderList,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    enableColumnResizing: enableColumnResize,
    columnResizeMode: 'onChange',
    defaultColumn: { minSize: 40 },
    state: {
      columnOrder,
      ...(columnSizing ? { columnSizing } : {}),
    },
    onColumnOrderChange: setColumnOrder,
    onColumnSizingChange: handleColumnSizingChange,
  });

  const sensors = useSensors(
    useSensor(MouseSensor),
    useSensor(TouchSensor),
    useSensor(KeyboardSensor),
  );

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (active && over && active.id !== over.id) {
      setColumnOrder((prev) => {
        const oldIndex = prev.indexOf(active.id as string);
        const newIndex = prev.indexOf(over.id as string);
        return arrayMove(prev, oldIndex, newIndex);
      });
    }
  };

  const tableMinWidth = table.getTotalSize();

  const syncSplitHeaderScrollLeft = useCallback((scrollLeft: number) => {
    const headerContent = splitHeaderContentRef.current;
    if (!headerContent) return;

    const nextScrollLeft = Number.isFinite(scrollLeft) ? Math.max(0, scrollLeft) : 0;
    headerContent.style.transform = `translate3d(-${nextScrollLeft}px, 0, 0)`;
    headerContent.style.setProperty('--table-scroll-left', `${nextScrollLeft}px`);
  }, []);

  useLayoutEffect(() => {
    const scrollContainer = bodyScrollRef.current;
    if (!scrollContainer) return;

    const handleScroll = () => {
      syncSplitHeaderScrollLeft(scrollContainer.scrollLeft);
    };

    handleScroll();
    scrollContainer.addEventListener('scroll', handleScroll, { passive: true });

    return () => {
      scrollContainer.removeEventListener('scroll', handleScroll);
    };
  }, [syncSplitHeaderScrollLeft, tableMinWidth]);

  const renderSplitHeader = () => {
    const headerStyle: CSSProperties = {
      minWidth: tableMinWidth > 0 ? tableMinWidth : undefined,
      willChange: 'transform',
    };

    return (
      <div
        className="sticky max-w-full overflow-hidden bg-gr-50"
        style={{
          top: `var(--order-table-header-top, ${tableHeaderTop}px)`,
          zIndex: TABLE_SPLIT_HEADER_Z_INDEX,
          // compact 전(스크롤 최상단)엔 테이블(Figma radius 12)과 동일한 rounded-top, compact 되면 0 으로 보간.
          // --order-compact 기반이라 관찰자 state 토글 없이 떨림이 없다.
          borderTopLeftRadius: 'calc(12px - 12px * var(--order-compact, 0))',
          borderTopRightRadius: 'calc(12px - 12px * var(--order-compact, 0))',
        }}
      >
        <div ref={splitHeaderContentRef} className="w-full" style={headerStyle}>
          <table
            className="w-full table-fixed border-collapse overflow-hidden"
            style={{ minWidth: tableMinWidth > 0 ? tableMinWidth : undefined }}
          >
            <TableHeader
              table={table}
              columnOrder={columnOrder}
              isSticky={false}
              enableResize={enableColumnResize}
              onMeasureMin={reportHeaderMinSize}
            />
          </table>
        </div>
      </div>
    );
  };

  return (
    <DndContext
      modifiers={[restrictToHorizontalAxis]}
      collisionDetection={closestCenter}
      onDragEnd={handleDragEnd}
      sensors={sensors}
    >
      {/* flex-1 체인: 주문이 적어 카드에 빈 공간이 남아도 가로 스크롤 컨테이너를
          카드 하단 보더까지 늘려, 빈 공간에서도 좌우 스크롤이 되고 스크롤바가 카드 바닥에 붙는다. */}
      <div className="flex w-full min-w-0 max-w-full flex-1 flex-col">
        {renderSplitHeader()}
        <div
          ref={bodyScrollRef}
          className="scrollbar min-w-0 max-w-full flex-1 overflow-x-auto"
          data-membership-preview-interactive={isPreviewMode ? 'true' : undefined}
        >
          {/* overflow-hidden 을 두면 table 이 sticky scrollport 가 되어 체크박스 셀의
              left:0 고정이 깨진다. 제거해 bodyScrollRef(가로 스크롤)를 scrollport 로 삼는다.
              table-fixed: 컬럼 너비를 getSize()로 강제해 셀 내용이 폭을 밀어내지 못하게 한다. */}
          <table
            className="w-full table-fixed border-collapse"
            style={{ minWidth: tableMinWidth > 0 ? tableMinWidth : undefined }}
          >
            <TableBody
              table={table}
              checkedOrders={checkedOrders}
              columnOrder={columnOrder}
              enableRowClickSelect={enableRowClickSelect}
            />
          </table>
          {bodyFooter}
        </div>
      </div>
    </DndContext>
  );
};

export default MarketTable;
